import Vue from "vue";
import _ from "lodash";
import DSRCmessage from "@/helpers/DSRCmessage.js";
import API from "@/api/index";
import * as Geo from "@/helpers/MHIGeometry.js";
import RsuStore from "@/store/modules/rsu";
import pointInPolygon from "point-in-polygon";

const MODULE_NAME = "fvu_monitor";

const MUTATION_TYPES = {
	APPLY_DATA: "APPLY_DATA",
	APPLY_WSA_DATA: "APPLY_WSA_DATA",
	CLEAR_DATA: "CLEAR_DATA",
	DELETE_LOCATION: "DELETE_LOCATION",
	LATEST_MONITOR_FRAME_UPDATE: "LATEST_MONITOR_FRAME_UPDATE",
	LOCATION_HISTORY_UPDATE: "LOCATION_HISTORY_UPDATE",
	MARK_VU_APPROACH_CLOSED: "MARK_VU_APPROACH_CLOSED",
	MARK_VU_APPROACH_INVALID: "MARK_VU_APPROACH_INVALID",
	MARK_VU_APPROACH_RESULT_LOADING: "MARK_VU_APPROACH_RESULT_LOADING",
	MARK_VU_APPROACH_RESULT_LOADED: "MARK_VU_APPROACH_RESULT_LOADED",
	SET_APPROACH_TEST_RESULT: "SET_APPROACH_TEST_RESULT",
	SET_COMPLETION_APPROACH_RUN: "SET_COMPLETION_APPROACH_RUN",
	SET_NOW: "SET_NOW",
	SET_START_TIME: "SET_START_TIME",
	SET_TEST_CONFIG: "SET_TEST_CONFIG",
	SET_RSU_TEST_CONFIG: "SET_RSU_TEST_CONFIG",
	VU_APPROACHES_UPDATE: "VU_APPROACHES_UPDATE"
};

const ACTION_TYPES = {
	INGEST_MESSAGES: "INGEST_MESSAGES",
	LOAD_APPROACH_TEST_RESULTS: "LOAD_APPROACH_TEST_RESULTS",
	LOAD_MESSAGE_COUNTS_BY_APPROACH_DISTANCE:
		"LOAD_MESSAGE_COUNTS_BY_APPROACH_DISTANCE",
	LOAD_OVERALL_INTERSECTION_RESULT: "LOAD_OVERALL_INTERSECTION_RESULT",
	LOAD_RSU_TEST_CONFIG: "LOAD_RSU_TEST_CONFIG",
	LOAD_VU_APPROACHES: "LOAD_VU_APPROACHES",
	LOAD_APPROACH_RUNS_FOR_TESTSETNUM: "LOAD_APPROACH_RUNS_FOR_TESTSETNUM",
	MARK_VU_APPROACH_RESULTS_LOADING: "MARK_VU_APPROACH_RESULTS_LOADING",
	MARK_VU_APPROACH_RESULTS_LOADED: "MARK_VU_APPROACH_RESULTS_LOADED",
	SET_TEST_CONFIG_FROM_TEST_RESULT: "SET_TEST_CONFIG_FROM_TEST_RESULT",
	SET_TEST_CONFIG_FROM_TESTSETNUM: "SET_TEST_CONFIG_FROM_TESTSETNUM",
	CLEANUP: "CLEANUP"
};

const MODULE_CONFIG = {
	HASH_RESOLUTION: 0.00005,
	MAX_LOCATIONS: Math.pow(10, 5),
	MIN_NON_STATIONARY_SPEED: 5,
	GPS_ACCURACY_MULTIPLIER: 2,
	GPS_MAX_CONTINUOUS_TIME_SEC: 2,
	GPS_MAX_DISTANCE_FUDGE_FACTOR: 2,
	LATEST_MONITOR_FRAME_TIMEOUT_SECS: 5,
	VALIDATION_FREQUENCY_PRECISION: 3,
	FVU_MONITOR_REGEXP: /^v1\/fvu\/([^/]+)\/([^/]+)\/monitor/i,
	PARTIAL_PARSER_REGEXP: /^v1\/fvu\/[^/]+\/[^/]+\/dsrc\/partial/i,
	GPS_REGEXP: /^v1\/(.*)\/(.*)\/gps\/stateChanged\/periodicData$/,
	DEVICE_MESSAGE_REGEXP: /^v1\/fvu\/([^/]+)\/([^/]+)\/devicemessage/i,
	FALLBACK_APPROACH: {
		vuapproachesid: -1,
		approachtype: "complete",
		geoJSON: { coordinates: [] }
	}
};

export default {
	MODULE_NAME,
	MUTATION_TYPES,
	ACTION_TYPES,
	MODULE_CONFIG,
	namespaced: true,
	state: {
		startTime: null,
		latest_monitor_frame: {},
		location_history: [],
		test_config: {},
		now: null,
		vu_approaches: {},
		vu_approach_location_reference: { rsuId: {}, intersectionId: {} },
		rsu_data: {},
		rsu_test_config: {}
	},
	mutations: {
		[MUTATION_TYPES.APPLY_DATA](state, payload) {
			if (!state.rsu_data[payload.rsuId]) {
				Vue.set(state.rsu_data, payload.rsuId, {});
			}
			const vuApproachLocationId = payload.vuApproachLocationId;
			if (!vuApproachLocationId) {
				console.warn(
					`${MODULE_NAME}/${MUTATION_TYPES.APPLY_DATA}: Missing or invalid vuApproachLocationId in payload.`
				);
				return false;
			}
			const approach =
				payload.vuapproachesid == MODULE_CONFIG.FALLBACK_APPROACH.vuapproachesid
					? MODULE_CONFIG.FALLBACK_APPROACH
					: state.vu_approaches[vuApproachLocationId] &&
					  state.vu_approaches[vuApproachLocationId][payload.vuapproachesid];

			if (!approach) {
				if (payload.vuapproachesid > 0) {
					console.warn(
						`${MODULE_NAME}/${MUTATION_TYPES.APPLY_DATA}: Approach not found for payload: { vuapproachesid: ${payload.vuapproachesid},... }`
					);
				}
				return false;
			}

			if (approach.parentapproachid) {
				// This mutation shouldn't be called for the sub-zones, but if it is, do nothing.
				return false;
			}

			if (!state.rsu_data[payload.rsuId][payload.vuapproachesid]) {
				Vue.set(state.rsu_data[payload.rsuId], payload.vuapproachesid, {
					vuapproachesid: payload.vuapproachesid,
					latlngHash: payload.latlngHash,
					gpsCount: 0,
					freqCount: 0,
					freqTotal: 0,
					idleCount: 0,
					parserErrorCount: 0,
					parserFormatErrorCount: 0,
					parserContentErrorCount: 0,
					parserCount: 0,
					latestTimestamp: null,
					enteredAt: null,
					preExitedAt: null,
					exitedAt: null,
					approachComplete: false
				});
			}

			const approachData =
				state.rsu_data[payload.rsuId][payload.vuapproachesid];
			const msgTypeData = DSRCmessage.get(payload.typeId);
			const msgType = msgTypeData.slug;
			if (!approachData[msgType]) {
				approachData[msgType] = {
					gpsCount: approachData.gpsCount,
					freqCount: 0,
					freqTotal: 0,
					idleCount: 0,
					parserErrorCount: 0,
					parserFormatErrorCount: 0,
					parserContentErrorCount: 0,
					parserCount: 0
				};
			}
			if (
				_.isEmpty(payload.frequencyChecker) &&
				payload.vuapproachesid != MODULE_CONFIG.FALLBACK_APPROACH.vuapproachesid
			) {
				approachData.idleCount++;
				approachData[msgType].idleCount++;
			}
			_.each(["partialParser", "frequencyChecker"], srcKey => {
				_.each(payload[srcKey], srcData => {
					const srcDataTimestamp = new Date(srcData.Timestamp).getTime();
					if (
						payload.vuapproachesid !=
							MODULE_CONFIG.FALLBACK_APPROACH.vuapproachesid &&
						(!approachData.enteredAt ||
							srcDataTimestamp < approachData.enteredAt ||
							(approachData.approachComplete &&
								srcDataTimestamp > approachData.preExitedAt))
					) {
						// This data is outside the window we're considering, so discard it.
						return;
					}
					setErrorUnheardFlags(srcData);
					if (!srcData.isUnheard) {
						approachData.latestTimestamp = _.max([
							srcDataTimestamp,
							approachData.latestTimestamp
						]);
					}

					switch (srcKey) {
						case "frequencyChecker":
							srcData.WindowSizeSeconds = srcData.WindowSizeSeconds || 1;
							if (srcData.isUnheard) {
								approachData.idleCount++;
								approachData[msgType].idleCount++;
							} else {
								approachData.freqCount += srcData.WindowSizeSeconds;
								approachData[msgType].freqCount += srcData.WindowSizeSeconds;
								approachData.freqTotal +=
									srcData.FrequencyHz * srcData.WindowSizeSeconds;
								approachData[msgType].freqTotal +=
									srcData.FrequencyHz * srcData.WindowSizeSeconds;
							}
							break;
						case "partialParser":
							approachData.parserCount++;
							approachData[msgType].parserCount++;

							if (srcData.isError) {
								approachData.parserErrorCount++;
								approachData[msgType].parserErrorCount++;
								if (!srcData.IsCorrectFormat) {
									approachData.parserFormatErrorCount++;
									approachData[msgType].parserFormatErrorCount++;
								}
								if (!srcData.IsCorrectContent) {
									approachData.parserContentErrorCount++;
									approachData[msgType].parserContentErrorCount++;
								}
							}
							break;
					}
				});
			});
		},
		[MUTATION_TYPES.APPLY_WSA_DATA](state, { vuApproach, rsu, payload }) {
			const rsuId = rsu.deviceId;
			const vuapproachesid = vuApproach.vuapproachesid;
			if (!state.rsu_data[rsuId]) return false;
			if (!state.rsu_data[rsuId][vuapproachesid]) {
				Vue.set(state.rsu_data[rsuId], vuapproachesid, {
					vuapproachesid: vuapproachesid,
					gpsCount: 0,
					freqCount: 0,
					freqTotal: 0,
					idleCount: 0,
					parserErrorCount: 0,
					parserFormatErrorCount: 0,
					parserContentErrorCount: 0,
					parserCount: 0,
					latestTimestamp: null,
					enteredAt: null,
					preExitedAt: null,
					exitedAt: null,
					approachComplete: false
				});
			}

			const approachData = state.rsu_data[rsuId][vuapproachesid];
			if (!approachData.WSA) {
				approachData.WSA = {
					gpsCount: approachData.gpsCount,
					freqCount: 0,
					freqTotal: 0,
					idleCount: 0,
					parserErrorCount: 0,
					parserFormatErrorCount: 0,
					parserContentErrorCount: 0,
					parserCount: 0
				};
			}

			approachData.WSA.gpsCount = approachData.gpsCount;

			const count = parseInt(payload.count, 10) || 0;
			approachData.parserCount += count;
			approachData.WSA.parserCount += count;
		},
		[MUTATION_TYPES.CLEAR_DATA](state) {
			Vue.set(state, "latest_monitor_frame", {});
			Vue.set(state, "location_history", []);
			Vue.set(state, "rsu_data", {});
			Vue.set(state, "test_config", {});
			Vue.set(state, "now", null);
		},
		[MUTATION_TYPES.DELETE_LOCATION](state) {
			if (state.location_history.length) {
				state.location_history.shift();
			}
		},
		[MUTATION_TYPES.LATEST_MONITOR_FRAME_UPDATE](state, payload) {
			try {
				if (
					state.latest_monitor_frame &&
					state.latest_monitor_frame.timestamp &&
					state.latest_monitor_frame.timestamp > payload.timestamp
				) {
					throw new Error(
						MODULE_NAME +
							"/" +
							MUTATION_TYPES.LATEST_MONITOR_FRAME_UPDATE +
							": rejecting incoming frame because its timestamp is older than the previous frame."
					);
				}
				Vue.set(state, "latest_monitor_frame", payload);
			} catch (e) {
				if (!payload.expired) console.debug(e);
				return false;
			}
		},
		[MUTATION_TYPES.LOCATION_HISTORY_UPDATE](
			state,
			{ gpsData, timestamp, getRsu, getApproachesForPoint }
		) {
			if (timestamp instanceof Date) timestamp = timestamp.getTime();
			if (!_.isNumber(timestamp)) timestamp = new Date(timestamp).getTime();
			const latlngHash = getLatLngHash({ gpsData });
			const tail = [];
			const candidate = { latlngHash };
			while (
				state.location_history.length &&
				state.location_history[state.location_history.length - 1].timestamp >
					timestamp
			) {
				tail.unshift(state.location_history.pop());
			}
			if (state.location_history.length) {
				const last = state.location_history[state.location_history.length - 1];
				if (gpsData.speed < MODULE_CONFIG.MIN_NON_STATIONARY_SPEED) {
					candidate.stationary = true;
				} else if (!gpsDifferentEnough(last, { gpsData, timestamp })) {
					candidate.tooCloseTo = last.latlngHash;
				} else if (gpsTooDifferent(last, { gpsData, timestamp })) {
					candidate.tooFarFrom = last.latlngHash;
				}
			}
			state.location_history.push({
				gpsData,
				timestamp,
				...candidate
			});
			if (tail.length) state.location_history.push(...tail);

			// Now see whether we should set any subapproach timestamps based on this data
			if (!getRsu || !getApproachesForPoint) {
				return;
			}
			_.each(state.rsu_data, (rsuData, rsuId) => {
				const rsu = getRsu(rsuId);
				if (!rsu) {
					return;
				}
				const approachesForPoint = getApproachesForPoint({
					...rsu,
					...gpsData,
					filterFn: approach =>
						!(
							rsuData[approach.parentapproachid] &&
							rsuData[approach.parentapproachid].exitedAt &&
							rsuData[approach.parentapproachid].enteredAt &&
							rsuData[approach.parentapproachid].exitedAt > timestamp &&
							rsuData[approach.parentapproachid].enteredAt < timestamp
						)
				}).sort(
					(a, b) => (b.parentapproachid || -10) - (a.parentapproachid || -10)
				);
				if (!approachesForPoint.length) {
					approachesForPoint.push(MODULE_CONFIG.FALLBACK_APPROACH);
				}
				_.each(approachesForPoint, vuApproach => {
					const parentapproachid =
						vuApproach.parentapproachid || vuApproach.vuapproachesid;
					if (!rsuData[parentapproachid]) {
						Vue.set(rsuData, parentapproachid, {
							vuapproachesid: parentapproachid,
							gpsCount: 0,
							freqCount: 0,
							freqTotal: 0,
							idleCount: 0,
							parserErrorCount: 0,
							parserFormatErrorCount: 0,
							parserContentErrorCount: 0,
							parserCount: 0,
							latestTimestamp: null,
							enteredAt: null,
							preExitedAt: null,
							exitedAt: null,
							approachComplete: false
						});
					}
					const approachData = rsuData[parentapproachid];
					if (vuApproach.approachtype == "complete") {
						if (!approachData.approachComplete) {
							// Increment gpsCount for this approach
							approachData.gpsCount += 1;
							// Update gpsCount for any msg types in this approach
							_.each(approachData, (val, key) => {
								const msgTypeData = DSRCmessage.get(key);
								if (!msgTypeData) return;
								val.gpsCount = approachData.gpsCount;
							});
						}
					} else if (vuApproach.approachtype == "entrance") {
						approachData.enteredAt = approachData.enteredAt
							? _.min([approachData.enteredAt, timestamp])
							: timestamp;
					} else if (
						vuApproach.approachtype == "preexit" &&
						approachData.enteredAt
					) {
						approachData.preExitedAt = _.max([
							timestamp,
							approachData.preExitedAt
						]);
					} else if (
						vuApproach.approachtype == "postexit" &&
						approachData.preExitedAt
					) {
						approachData.exitedAt = approachData.exitedAt
							? _.min([approachData.exitedAt, timestamp])
							: timestamp;
					}
				});
			});
		},
		[MUTATION_TYPES.MARK_VU_APPROACH_CLOSED](
			state,
			{
				vuApproachLocationId,
				approachRunId,
				vuapproachesid,
				timestamp,
				validated,
				validationDetails,
				rsuId,
				approachtestid
			}
		) {
			const vuApproach = _.reduce(
				vuApproachLocationId
					? [state.vu_approaches[vuApproachLocationId]]
					: state.vu_approaches,
				(r, vuApproaches) => {
					return r || vuApproaches[vuapproachesid];
				},
				null
			);
			timestamp = timestamp
				? new Date(timestamp).getTime()
				: new Date().getTime();

			if (!vuApproach) {
				// We don't recognize this approach.
				return false;
			}
			if (rsuId && _.isUndefined(state.rsu_data[rsuId])) {
				Vue.set(state.rsu_data, rsuId, {});
			}
			_.each(state.rsu_data, (rsuData, deviceId) => {
				if (!rsuData[vuapproachesid] && rsuId && rsuId == deviceId) {
					Vue.set(state.rsu_data[rsuId], vuapproachesid, {});
				}
				const approachData = rsuData[vuapproachesid];
				if (!approachData) {
					// We don't have anything for this approach on this rsu, either because we haven't loaded it yet or because it's in the wrong intersection.
					return;
				}
				if (!approachData.approachComplete) {
					// See if the times line up, and if so, mark this complete
					if (state.startTime && state.startTime < timestamp) {
						Vue.set(approachData, "approachComplete", true);
						Vue.set(approachData, "exitedAt", timestamp);
						Vue.set(approachData, "completionApproachRunId", approachRunId);
						Vue.set(approachData, "resultPending", true);
					}
				}
				if (!approachData.approachRuns) {
					Vue.set(approachData, "approachRuns", {});
				}
				if (
					!approachData.approachRuns[approachRunId] ||
					approachData.approachRuns[approachRunId].timestamp != timestamp
				) {
					Vue.set(approachData.approachRuns, approachRunId, {
						approachRunId,
						timestamp,
						validated,
						validationDetails,
						resultData: null,
						approachtestid: approachtestid
					});
				}
			});
		},
		[MUTATION_TYPES.MARK_VU_APPROACH_INVALID](
			state,
			{ approachRunId, vuapproachesid, timestamp, validationDetails, validated }
		) {
			const vuApproach = _.reduce(
				state.vu_approaches,
				(r, vuApproaches) => {
					return r || vuApproaches[vuapproachesid];
				},
				null
			);
			timestamp = timestamp
				? new Date(timestamp).getTime()
				: new Date().getTime();

			if (!vuApproach) {
				// We don't recognize this approach.
				return false;
			}
			_.each(state.rsu_data, rsuData => {
				const approachData = rsuData[vuapproachesid];
				if (!approachData) {
					// We don't have anything for this approach on this rsu, either because we haven't loaded it yet or because it's in the wrong intersection.
					return;
				}
				if (!approachData.approachRuns) {
					Vue.set(approachData, "approachRuns", {});
				}
				if (
					!approachData.approachRuns[approachRunId] ||
					approachData.approachRuns[approachRunId].timestamp != timestamp
				) {
					Vue.set(approachData.approachRuns, approachRunId, {
						approachRunId,
						timestamp,
						validated,
						validationDetails,
						resultData: null
					});
				}
			});
		},
		[MUTATION_TYPES.MARK_VU_APPROACH_RESULT_LOADING](
			state,
			{ vuapproachesid, rsuId }
		) {
			Vue.set(state.rsu_data[rsuId][vuapproachesid], "resultPending", false);
			Vue.set(state.rsu_data[rsuId][vuapproachesid], "resultLoading", true);
		},
		[MUTATION_TYPES.MARK_VU_APPROACH_RESULT_LOADED](
			state,
			{ rsuId, vuapproachesid }
		) {
			Vue.set(state.rsu_data[rsuId][vuapproachesid], "resultLoading", false);
			Vue.set(state.rsu_data[rsuId][vuapproachesid], "resultLoaded", true);
		},
		[MUTATION_TYPES.SET_APPROACH_TEST_RESULT](
			state,
			{
				rsuId,
				vuapproachesid,
				approachRunId,
				approachRunTestResult,
				approachRunNonTestableCounts,
				approachtestid
			}
		) {
			Vue.set(
				state.rsu_data[rsuId][vuapproachesid].approachRuns[approachRunId],
				"resultData",
				approachRunTestResult
			);
			Vue.set(
				state.rsu_data[rsuId][vuapproachesid].approachRuns[approachRunId],
				"nonTestableTypeCounts",
				approachRunNonTestableCounts
			);
			Vue.set(
				state.rsu_data[rsuId][vuapproachesid].approachRuns[approachRunId],
				"approachtestid",
				approachtestid
			);
		},
		[MUTATION_TYPES.SET_COMPLETION_APPROACH_RUN](
			state,
			{ rsuId, vuapproachesid, approachRunId }
		) {
			Vue.set(
				state.rsu_data[rsuId][vuapproachesid],
				"completionApproachRunId",
				approachRunId
			);

			const approachRunData =
				state.rsu_data[rsuId][vuapproachesid].approachRuns[approachRunId];
			const approachRunTestResult =
				(approachRunData && approachRunData.resultData) || [];
			const approachRunNonTestableCounts =
				(approachRunData && approachRunData.nonTestableTypeCounts) || [];

			// reset counts so we can be sure we didn't miss anything in the next step; also handles the "clear" case
			Vue.set(state.rsu_data[rsuId][vuapproachesid], "gpsCount", 0);
			Vue.set(state.rsu_data[rsuId][vuapproachesid], "parserCount", 0);
			Vue.set(state.rsu_data[rsuId][vuapproachesid], "parserErrorCount", 0);
			Vue.set(state.rsu_data[rsuId][vuapproachesid], "enteredAt", null);
			Vue.set(state.rsu_data[rsuId][vuapproachesid], "preExitedAt", null);
			Vue.set(state.rsu_data[rsuId][vuapproachesid], "exitedAt", null);
			_.each(state.rsu_data[rsuId][vuapproachesid], (val, key) => {
				const msgTypeData = DSRCmessage.get(key);
				if (!msgTypeData) return;
				Vue.set(state.rsu_data[rsuId][vuapproachesid][key], "gpsCount", 0);
				Vue.set(state.rsu_data[rsuId][vuapproachesid][key], "parserCount", 0);
				Vue.set(
					state.rsu_data[rsuId][vuapproachesid][key],
					"parserErrorCount",
					0
				);
				Vue.set(
					state.rsu_data[rsuId][vuapproachesid][key],
					"resultDetail",
					null
				);
			});

			// Update counts and timestamps for approach; update counts and test results for all message types as well.
			_.each(approachRunTestResult, testResultRow => {
				const msgTypeData =
					testResultRow.messageTypeId &&
					DSRCmessage.get(testResultRow.messageTypeId);
				var target = state.rsu_data[rsuId][vuapproachesid];
				if (msgTypeData) {
					const msgType = msgTypeData.slug;
					if (!state.rsu_data[rsuId][vuapproachesid][msgType]) {
						state.rsu_data[rsuId][vuapproachesid][msgType] = {};
					}
					target = state.rsu_data[rsuId][vuapproachesid][msgType];
				} else if (testResultRow.messageTypeId !== null) {
					console.warn(
						`${MODULE_NAME}/${MUTATION_TYPES.MARK_VU_APPROACH_RESULT_LOADED}: Invalid or unknown messageTypeId in approachRunTestResult row: ${testResultRow.messageTypeId}`
					);
					return;
				} else {
					state.rsu_data[rsuId][vuapproachesid].enteredAt =
						testResultRow.actualStart &&
						new Date(testResultRow.actualStart).getTime();
					state.rsu_data[rsuId][vuapproachesid].exitedAt =
						testResultRow.actualEnd &&
						new Date(testResultRow.actualEnd).getTime();
				}
				target.gpsCount = testResultRow.timeInApproach;
				target.parserCount = testResultRow.parserCount;
				target.parserErrorCount = testResultRow.parserErrorCount;
				target.resultDetail = testResultRow.resultDetail;

				if (testResultRow.testResult == "N/A") {
					target.testResult = "FAIL";
				} else target.testResult = testResultRow.testResult;
			});
			_.each(approachRunNonTestableCounts, ({ msgType, count, errorCount }) => {
				if (!state.rsu_data[rsuId][vuapproachesid][msgType]) {
					state.rsu_data[rsuId][vuapproachesid][msgType] = {};
				}
				state.rsu_data[rsuId][vuapproachesid][msgType].gpsCount =
					state.rsu_data[rsuId][vuapproachesid].gpsCount;
				state.rsu_data[rsuId][vuapproachesid][msgType].parserCount = count;
				state.rsu_data[rsuId][vuapproachesid][msgType].parserErrorCount =
					errorCount || 0;
			});
		},
		[MUTATION_TYPES.SET_NOW](state, payload) {
			Vue.set(state, "now", payload);
		},
		[MUTATION_TYPES.SET_START_TIME](state, payload) {
			if (_.isUndefined(payload) || payload === null) payload = new Date();
			if (!(payload instanceof Date)) payload = new Date(payload);
			state.startTime = payload.getTime();
		},
		[MUTATION_TYPES.SET_TEST_CONFIG](state, payload) {
			Vue.set(state, "test_config", payload);
		},
		[MUTATION_TYPES.SET_RSU_TEST_CONFIG](state, payload) {
			if (!payload.rsuId) {
				throw new Error(
					'Missing or invalid rsuId in payload of "' +
						MODULE_NAME +
						"/" +
						MUTATION_TYPES.SET_RSU_TEST_CONFIG +
						'".'
				);
			}
			Vue.set(state.rsu_test_config, payload.rsuId, payload);
		},
		[MUTATION_TYPES.VU_APPROACHES_UPDATE](
			state,
			{ rsuId, intersectionId, vuApproaches, vuApproachLocationId }
		) {
			if (_.isArray(vuApproaches)) {
				vuApproaches = _.reduce(
					vuApproaches,
					(r, vuApproach) => {
						r[vuApproach.vuapproachesid] = vuApproach;
						return r;
					},
					{}
				);
			}
			if (!_.isObject(vuApproaches)) {
				throw new Error(
					`${MODULE_NAME}/${MUTATION_TYPES.VU_APPROACHES_UPDATE}: Invalid or missing vuApproaches parameter.`
				);
			}
			vuApproachLocationId =
				vuApproachLocationId ||
				_.reduce(
					vuApproaches,
					(vuApproachLocationId, vuApproach) => {
						vuApproachLocationId =
							vuApproachLocationId || vuApproach.vuApproachLocationId;
						if (vuApproachLocationId !== vuApproach.vuApproachLocationId) {
							throw new Error(
								`${MODULE_NAME}/${MUTATION_TYPES.VU_APPROACHES_UPDATE}: vuApproachLocationId mismatch.`
							);
						}
						return vuApproachLocationId;
					},
					null
				);
			if (!vuApproachLocationId) {
				throw new Error(
					`${MODULE_NAME}/${MUTATION_TYPES.VU_APPROACHES_UPDATE}: Missing or invalid vuApproachLocationId in vuApproaches.`
				);
			}
			if (rsuId) {
				Vue.set(
					state.vu_approach_location_reference.rsuId,
					rsuId,
					vuApproachLocationId
				);
			}
			if (intersectionId) {
				Vue.set(
					state.vu_approach_location_reference.intersectionId,
					intersectionId,
					vuApproachLocationId
				);
			}
			vuApproaches[MODULE_CONFIG.FALLBACK_APPROACH.vuapproachesid] =
				MODULE_CONFIG.FALLBACK_APPROACH;
			Vue.set(state.vu_approaches, vuApproachLocationId, vuApproaches);
		}
	},
	actions: {
		[ACTION_TYPES.INGEST_MESSAGES](context, payload) {
			const getRsu =
				context.rootGetters[RsuStore.MODULE_NAME + "/byIdentifier"];
			return new Promise((resolve, reject) => {
				// validate topic string
				if (!payload.topic || !payload.topic.toString()) {
					console.warn(
						`${MODULE_NAME}/${ACTION_TYPES.INGEST_MESSAGES}: Invalid payload topic.`,
						payload
					);
					return reject(
						new Error(
							`${MODULE_NAME}/${ACTION_TYPES.INGEST_MESSAGES}: Invalid MQTT topic.`
						)
					);
				}
				// convert timestamp, if present
				try {
					if (payload.timestamp && !_.isNumber(payload.timestamp)) {
						payload.timestamp = new Date(payload.timestamp).getTime();
					}
				} catch (e) {
					console.warn(e);
				}
				// Is this an approachrun/complete message?
				if (
					context.getters.fvuMonitorRegexp.exec(payload.topic) &&
					payload.topic.match(/approachrun\/complete$/)
				) {
					if (!(payload.approachrunid && payload.approachid)) {
						return reject(
							new Error(
								`${MODULE_NAME}/${ACTION_TYPES.INGEST_MESSAGES}: Invalid message on topic "${payload.topic}".`
							)
						);
					}
					if (payload.validated) {
						context.commit(MUTATION_TYPES.MARK_VU_APPROACH_CLOSED, {
							vuApproachLocationId: payload.vuApproachLocationId,
							approachRunId: payload.approachrunid,
							vuapproachesid: payload.approachid,
							timestamp: payload.timestamp,
							validated: payload.validated,
							validationDetails: payload.validationDetails
						});
						return setTimeout(
							() =>
								resolve(
									context.dispatch(ACTION_TYPES.LOAD_APPROACH_TEST_RESULTS)
								),
							10
						);
					} else {
						console.debug("Invalid approach: %o", payload);
						return setTimeout(
							() =>
								resolve(
									context.commit(MUTATION_TYPES.MARK_VU_APPROACH_INVALID, {
										approachRunId: payload.approachrunid,
										vuapproachesid: payload.approachid,
										timestamp: payload.timestamp,
										validated: payload.validated,
										validationDetails: payload.validationDetails
									})
								),
							10
						);
					}
				}
				// Is it a devicemessages frame?
				if (context.getters.deviceMessageRegexp.exec(payload.topic)) {
					if (payload.topic.match(/\/wsa$/)) {
						// This is WSA data; store it in the same format as the other message types
						const rsu = getRsu(payload.RSUId);
						if (!rsu) {
							// This is about an rsu we don't know about; bail out
							return resolve(payload);
						}
						const vuApproaches = context.getters.approachesForPoint({
							filterFn: approach => approach.approachtype == "complete",
							...rsu,
							latitude: payload.gps_end.lat,
							longitude: payload.gps_end.lon
						});
						if (!vuApproaches.length) {
							vuApproaches.push(MODULE_CONFIG.FALLBACK_APPROACH);
						}
						vuApproaches.sort(
							(a, b) => a.parentapproachid || 0 - b.parentapproachid || 0
						);
						_.each(vuApproaches, vuApproach => {
							context.commit(MUTATION_TYPES.APPLY_WSA_DATA, {
								rsu,
								vuApproach,
								payload
							});
						});
						return resolve(true);
					} else {
						return reject(
							new Error(
								`${MODULE_NAME}/${ACTION_TYPES.INGEST_MESSAGES}: Unrecognized topic: "${payload.topic}".`
							)
						);
					}
				}
				// Then is it a monitor frame or raw gps data?
				if (!payload.gpsData) {
					return reject(
						new Error(
							`${MODULE_NAME}/${ACTION_TYPES.INGEST_MESSAGES}: Invalid payload.`
						)
					);
				}
				const baseArgs = {
					latlngHash: getLatLngHash(payload)
				};
				if ("gpsData" in payload && "timestamp" in payload) {
					const extraOpts = {};
					if (context.getters.gpsRegexp.exec(payload.topic)) {
						// This is pure gps data, and we should do the gpsCount logic.
						extraOpts.getRsu = getRsu;
						extraOpts.getApproachesForPoint =
							context.getters.approachesForPoint;
					}
					context.commit(MUTATION_TYPES.LOCATION_HISTORY_UPDATE, {
						...extraOpts,
						...payload
					});
				}
				if (_.isArray(payload.type)) {
					// This is a frame from the monitor service, store it as the latest of same
					context.commit(MUTATION_TYPES.LATEST_MONITOR_FRAME_UPDATE, payload);
					// Set this monitor frame to expire
					setTimeout(
						() =>
							context.commit(MUTATION_TYPES.LATEST_MONITOR_FRAME_UPDATE, {
								expired: true,
								...payload
							}),
						context.getters.LATEST_MONITOR_FRAME_TIMEOUT_SECS * 1000
					);
					const frameRsus = context.getters.latestMonitorFrameRSUs;
					_.each(payload.type, typeData => {
						const typeArgs = { typeId: typeData.id };
						_.each(typeData.messages, messageData => {
							const rsus = [];
							const thisRsu = getRsu(messageData.rsuId);
							if (thisRsu) rsus.push(thisRsu);
							if (!thisRsu && typeData.id == 20) {
								// This is BSM data, which doesn't get assigned an RSU id, so we'll credit it to any RSUs that we heard from elsewhere in this frame.
								rsus.push(...frameRsus);
							}
							_.each(rsus, rsu => {
								const vuApproaches = context.getters.approachesForPoint({
									filterFn: approach => approach.approachtype == "complete",
									...rsu,
									...payload.gpsData
								});
								if (!vuApproaches.length) {
									vuApproaches.push(MODULE_CONFIG.FALLBACK_APPROACH);
								}
								vuApproaches.sort(
									(a, b) => a.parentapproachid || 0 - b.parentapproachid || 0
								);
								_.each(vuApproaches, vuApproach => {
									const approachArgs = {
										intersectionId: rsu.intersectionId,
										vuapproachesid: vuApproach.vuapproachesid
									};
									const vuApproachLocationId = context.getters.vuApproachLocationId(
										rsu
									);
									context.commit(MUTATION_TYPES.APPLY_DATA, {
										vuApproachLocationId,
										...approachArgs,
										...baseArgs,
										...typeArgs,
										...messageData,
										rsuId: rsu.deviceId
									});
								});
							});
						});
					});
				}
				return resolve(true);
			});
		},
		[ACTION_TYPES.LOAD_APPROACH_TEST_RESULTS](context) {
			const ret = {};
			_.each(
				context.getters.approachRunIdsToLoad,
				({ approachRunId, vuapproachesid, rsuId, approachtestid }) => {
					const nonTestableTypesStr = context.getters.nonTestableMessageTypes
						.map(t => t.slug)
						.join(",");
					const qString = API.buildQueryString(
						approachtestid
							? { approachtestid, nonTestableTypesStr }
							: { approachRunId, rsuId, nonTestableTypesStr }
					);
					if (ret[qString]) return; // Already covered this one.
					context.dispatch(ACTION_TYPES.MARK_VU_APPROACH_RESULTS_LOADING, {
						vuapproachesid
					});
					const flatTestParams = context.getters.testConfig(rsuId);
					const testParams = [];
					_.each(context.getters.testableMessageTypes, msgTypeData => {
						const msgType = msgTypeData.slug;
						const msgTypeExpected = _.isUndefined(
							flatTestParams[`${msgType}_expected`]
						)
							? context.rootGetters[`${RsuStore.MODULE_NAME}/shouldHaveType`]({
									rsuId,
									msgType
							  })
							: flatTestParams[`${msgType}_expected`];
						testParams.push({
							id: msgTypeData.id,
							validationFrequency:
								flatTestParams[`${msgType}_validation_frequency`],
							errorThresholdPct:
								flatTestParams[`${msgType}_error_threshold_pct`],
							expected: msgTypeExpected ? 1 : 0
						});
					});
					testParams.sort((a, b) => a.id - b.id); // Sorting matters so the backend can tell if this has already been tested
					ret[qString] = (approachtestid
						? // If an approachtestid exists:
						  API.getWithParamsJson(
								API.ENDPOINTS.GET_APPROACH_TEST_RESULTS,
								{ approachtestid, nonTestableTypesStr },
								{},
								e => {
									throw e;
								}
						  )
						: // If no approachtestid exists:
						  API.postJson(
								API.ENDPOINTS.GET_APPROACH_RUN_TEST_RESULTS + qString,
								testParams,
								{},
								e => {
									throw e;
								}
						  )
					)
						.then(json => {
							if (!_.isObject(json)) {
								throw new Error(`Invalid server response: ${json}`);
							} else if (json.retCode && json.retCode < 0) {
								throw new Error(`[Server error] ${json.retMsg}`);
							} else if (_.isArray(json.rows) && !json.rows.length) {
								throw new Error(`Empty server response.`);
							}
							return json;
						})
						.catch(e => {
							console.warn(
								`${MODULE_NAME}/${ACTION_TYPES.LOAD_APPROACH_TEST_RESULTS} error: ${e.message}`
							);
							return [];
						})
						.then(approachRunTestResult => {
							context
								.dispatch(ACTION_TYPES.MARK_VU_APPROACH_RESULTS_LOADED, {
									vuapproachesid,
									approachtestid: approachRunTestResult.approachtestid,
									approachRunTestResult: approachRunTestResult.rows,
									approachRunNonTestableCounts:
										approachRunTestResult.nonTestableTypeCounts,
									approachRunId
								})
								.then(() => {
									context.dispatch(
										ACTION_TYPES.SET_TEST_CONFIG_FROM_TEST_RESULT,
										approachRunTestResult.rows
									);
								})
								.catch(e => console.error(e));
							return approachRunTestResult;
						});
				}
			);
			return Promise.all(Object.values(ret));
		},
		[ACTION_TYPES.LOAD_MESSAGE_COUNTS_BY_APPROACH_DISTANCE](
			context,
			{ approachRunId }
		) {
			return API.getWithParamsJson(
				API.ENDPOINTS.GET_MESSAGE_COUNTS_BY_APPROACH_DISTANCE,
				{ approachRunId }
			)
				.then(json => {
					if (_.isObject(json)) {
						if (json.retCode && json.retCode < 0) {
							throw new Error(json.retMsg || "Invalid server response.");
						}
					}
					return json;
				})
				.catch(e => {
					console.warn(
						`${MODULE_NAME}/${ACTION_TYPES.LOAD_MESSAGE_COUNTS_BY_APPROACH_DISTANCE}: ${e.message}`
					);
					return [];
				});
		},
		[ACTION_TYPES.LOAD_OVERALL_INTERSECTION_RESULT](
			context,
			{ rsuId, deviceId }
		) {
			rsuId = rsuId || deviceId;
			const resultIds = [];
			_.each(context.state.rsu_data[rsuId], rsuData => {
				if (
					rsuData.completionApproachRunId &&
					rsuData.approachRuns[rsuData.completionApproachRunId] &&
					rsuData.approachRuns[rsuData.completionApproachRunId].approachtestid
				) {
					resultIds.push(
						rsuData.approachRuns[rsuData.completionApproachRunId].approachtestid
					);
				}
			});
			return API.postJson(
				API.ENDPOINTS.GENERATE_VU_FINAL_TEST,
				resultIds.map(i => {
					return { id: i };
				}),
				{},
				e => {
					throw e;
				}
			);
		},
		[ACTION_TYPES.LOAD_RSU_TEST_CONFIG](context, { rsuId }) {
			const rsuTestConfig = context.getters.rsuTestConfig(rsuId);
			// If we're already loading test config for this rsu, bail out.
			if (rsuTestConfig && rsuTestConfig.isLoading)
				return Promise.reject(
					new Error(
						MODULE_NAME +
							"/" +
							ACTION_TYPES.LOAD_RSU_TEST_CONFIG +
							": Already loading test config for RSU " +
							rsuId +
							"; aborting."
					)
				);

			// Note that this RSU is loading
			context.commit(MUTATION_TYPES.SET_RSU_TEST_CONFIG, {
				rsuId,
				isLoading: true
			});

			// At some point this Promise will be the result of an API call to whatever method pulls test parameters for a given RSU.
			const req = Promise.reject(
				new Error("RSU-specific test parameters are not yet implemented.")
			);

			return req
				.then(testConfig => {
					if (_.isEmpty(testConfig) || !_.isObject(testConfig)) {
						throw new Error(
							"Invalid or missing test parameters for rsu " + rsuId + "."
						);
					}
					return testConfig;
				})
				.catch(e => {
					console.warn(
						"Unable to load specific test parameters for RSU " + rsuId + ": ",
						e
					);
					return false;
				})
				.then(testConfig => {
					if (_.isObject(testConfig)) {
						// If we got an object from the request, update the state accordingly.
						context.commit(MUTATION_TYPES.SET_RSU_TEST_CONFIG, {
							rsuId,
							...testConfig
						});
					} else {
						context.commit(MUTATION_TYPES.SET_RSU_TEST_CONFIG, { rsuId });
					}
					return context.getters.rsuTestConfig(rsuId);
				});
		},
		[ACTION_TYPES.LOAD_VU_APPROACHES](
			context,
			{ rsuId, deviceId, intersectionId, vuApproachLocationId }
		) {
			rsuId = rsuId || deviceId;
			if (
				vuApproachLocationId &&
				!_.isUndefined(
					context.getters.approachesByVuApproachLocationId(vuApproachLocationId)
				)
			) {
				return Promise.resolve(
					context.getters.approachesByVuApproachLocationId(vuApproachLocationId)
				);
			}
			if (
				intersectionId &&
				!_.isUndefined(
					context.getters.approachesByIntersectionId(intersectionId)
				)
			) {
				return Promise.resolve(
					context.getters.approachesByIntersectionId(intersectionId)
				);
			}
			if (rsuId && !_.isUndefined(context.getters.rsuApproaches(rsuId))) {
				return Promise.resolve(context.getters.rsuApproaches(rsuId));
			}
			return (vuApproachLocationId // Load by vuApproachLocationId if known
				? API.getWithParamsJson(
						API.ENDPOINTS.GET_VUAPPROACHES_BY_VU_APPROACH_LOCATION_ID,
						{ vuApproachLocationId }
				  )
				: intersectionId // Fall back to intersectionId if known
				? API.getWithParamsJson(
						API.ENDPOINTS.GET_VUAPPROACHES_BY_INTERSECTION_ID,
						{
							intersectionId
						}
				  )
				: // Fall back to rsuId if nothing else is available
				  API.getWithParamsJson(API.ENDPOINTS.GET_VUAPPROACHES_BY_RSU_ID, {
						rsuId
				  })
			)
				.then(json => {
					if (!_.isArray(json)) {
						throw new Error(`Invalid server response: ${json}`);
					}
					return json;
				})
				.catch(e => {
					console.warn(
						`${MODULE_NAME}/${ACTION_TYPES.LOAD_VU_APPROACHES} error: ${e.message}`
					);
					return [];
				})
				.then(vuApproaches => {
					context.commit(MUTATION_TYPES.VU_APPROACHES_UPDATE, {
						rsuId,
						intersectionId,
						vuApproachLocationId,
						vuApproaches
					});

					return vuApproaches;
				});
		},
		[ACTION_TYPES.LOAD_APPROACH_RUNS_FOR_TESTSETNUM](
			{ commit },
			{ testsetnum }
		) {
			return API.getWithParamsJson(
				API.ENDPOINTS.APPROACH_RUNS_BY_TESTSETNUM,
				{ testsetnum },
				{},
				e => {
					throw e;
				}
			)
				.then(res => {
					if (!res.success || !_.isArray(res.rows)) {
						throw new Error(
							`${MODULE_NAME}/${ACTION_TYPES.APPROACH_RUNS_BY_TESTSETNUM}: Server error (${res.retMsg}).`
						);
					}
					const startTime = _.min(
						res.rows
							.map(row => row.actualStart)
							.filter(i => !!i)
							.map(start => new Date(start))
					);
					if (startTime) {
						commit(MUTATION_TYPES.SET_START_TIME, startTime);
					}
					_.each(res.rows, row => {
						commit(MUTATION_TYPES.MARK_VU_APPROACH_CLOSED, {
							...row,
							approachRunId: row.approachrunid,
							vuapproachesid: row.approachid,
							timestamp: new Date(row.timestamp).getTime(),
							rsuId: row.rsuDeviceID
						});
					});
					return res.rows;
				})
				.catch(e => {
					console.warn(e);
					return [];
				});
		},
		[ACTION_TYPES.MARK_VU_APPROACH_RESULTS_LOADING](
			{ state, commit },
			{ vuapproachesid }
		) {
			_.each(state.rsu_data, (rsuData, rsuId) =>
				_.each(rsuData, (approachData, approachid) => {
					if (
						vuapproachesid == approachid &&
						approachData.resultPending &&
						!approachData.resultLoaded
					) {
						commit(MUTATION_TYPES.MARK_VU_APPROACH_RESULT_LOADING, {
							vuapproachesid,
							rsuId
						});
					}
				})
			);
		},
		[ACTION_TYPES.MARK_VU_APPROACH_RESULTS_LOADED](
			{ state, commit },
			{
				vuapproachesid,
				approachRunId,
				approachRunTestResult,
				approachRunNonTestableCounts,
				approachtestid
			}
		) {
			_.each(state.rsu_data, (rsuData, rsuId) =>
				_.each(rsuData, (approachData, approachid) => {
					if (vuapproachesid == approachid) {
						commit(MUTATION_TYPES.SET_APPROACH_TEST_RESULT, {
							rsuId,
							vuapproachesid,
							approachRunId,
							approachRunTestResult,
							approachRunNonTestableCounts,
							approachtestid
						});
					}
					if (approachData.completionApproachRunId == approachRunId) {
						commit(MUTATION_TYPES.MARK_VU_APPROACH_RESULT_LOADED, {
							rsuId,
							vuapproachesid
						});
						// This mutation will also populate resultData in the approach data.
						commit(MUTATION_TYPES.SET_COMPLETION_APPROACH_RUN, {
							rsuId,
							vuapproachesid,
							approachRunId
						});
					}
				})
			);
		},
		[ACTION_TYPES.SET_TEST_CONFIG_FROM_TESTSETNUM](
			{ dispatch },
			{ testsetnum }
		) {
			return API.getWithParamsJson(
				API.ENDPOINTS.GET_TEST_CONFIG_BY_TESTSETNUM,
				{ testsetnum },
				{},
				e => {
					console.warn(e);
					return { success: false, rows: [] };
				}
			)
				.then(res => {
					if (res.success && res.rows && res.rows.length)
						return dispatch(
							ACTION_TYPES.SET_TEST_CONFIG_FROM_TEST_RESULT,
							res.rows
						);
					else {
						console.debug(res);
						throw new Error("Invalid server response.");
					}
				})
				.catch(e => {
					console.warn(
						`${MODULE_NAME}/${ACTION_TYPES.SET_TEST_CONFIG_FROM_TESTSETNUM}: ${e.message}`
					);
					console.debug(e);
				});
		},
		[ACTION_TYPES.SET_TEST_CONFIG_FROM_TEST_RESULT](
			{ commit },
			testResultRows
		) {
			return new Promise((resolve, reject) => {
				const test_config = {};
				_.each(testResultRows, row => {
					const msgTypeData = DSRCmessage.get(row.messageTypeId);
					const msgType = msgTypeData && msgTypeData.slug;
					if (!msgType) return;

					test_config[`${msgType}_validation_frequency`] =
						row.validationFrequency;
					test_config[`${msgType}_error_threshold_pct`] = row.errorThresholdPct;
					test_config[`${msgType}_expected`] = !!row.expected;
				});
				if (_.isEmpty(test_config))
					return reject(
						new Error(
							`${MODULE_NAME}/${ACTION_TYPES.SET_TEST_CONFIG_FROM_TEST_RESULT}: Unable to derive test config from given test result.`
						)
					);
				commit(MUTATION_TYPES.SET_TEST_CONFIG, test_config);
				return resolve(test_config);
			});
		},
		[ACTION_TYPES.CLEANUP](context, { protectedRsuIds }) {
			return new Promise((resolve, reject) => {
				// Overshoot by 10% so once we start hitting this we might not have to cleanup every single time.
				const targetCount = Math.ceil(0.9 * MODULE_CONFIG.MAX_LOCATIONS);
				var locationBuckets = context.getters.locationHistory;
				if (locationBuckets.length <= MODULE_CONFIG.MAX_LOCATIONS) {
					return reject(
						new Error(
							MODULE_NAME +
								"/" +
								ACTION_TYPES.CLEANUP +
								": " +
								locationBuckets.length +
								" TFM-VU location history items found out of " +
								MODULE_CONFIG.MAX_LOCATIONS +
								" allowed.  Nothing to do."
						)
					);
				}
				protectedRsuIds = _.reduce(
					protectedRsuIds,
					(r, i) => {
						r[i] = true;
						return r;
					},
					{}
				);

				while (locationBuckets.length && locationBuckets.length > targetCount) {
					context.commit(MUTATION_TYPES.DELETE_LOCATION);
				}
				return resolve(true);
			});
		}
	},
	getters: {
		// Constants
		TEST_RESULTS: () => {
			return {
				PASS: "PASS",
				FAIL: "FAIL",
				UNHEARD: "UNHEARD",
				NULL: "NULL"
			};
		},
		LATEST_MONITOR_FRAME_TIMEOUT_SECS: () =>
			MODULE_CONFIG.LATEST_MONITOR_FRAME_TIMEOUT_SECS,
		minNonStationarySpeed: () => MODULE_CONFIG.MIN_NON_STATIONARY_SPEED,
		// Regular Expressions
		fvuMonitorRegexp: () => MODULE_CONFIG.FVU_MONITOR_REGEXP,
		partialParserRegexp: () => MODULE_CONFIG.PARTIAL_PARSER_REGEXP,
		gpsRegexp: () => MODULE_CONFIG.GPS_REGEXP,
		deviceMessageRegexp: () => MODULE_CONFIG.DEVICE_MESSAGE_REGEXP,

		dsrcMessageTypes: () =>
			["SPAT", "MAP", "TIM" /*, "SSM"*/, "BSM", "WSA"].map(DSRCmessage.get),
		j2735MessageTypes: (state, getters) =>
			getters.dsrcMessageTypes.filter(t => t.standard == "J2735"),
		testableMessageTypes: (state, getters) =>
			getters.dsrcMessageTypes.filter(t => !!t.testable),
		nonTestableMessageTypes: (state, getters) =>
			getters.dsrcMessageTypes.filter(t => !t.testable),
		startTime: state => state.startTime,
		now: state => state.now || new Date().getTime(),
		approachRunIdsToLoad: state => {
			const ret = {};

			_.each(state.rsu_data, (rsuData, rsuId) =>
				_.each(rsuData, (approachData, vuapproachesid) =>
					_.each(approachData.approachRuns, approachRunData => {
						if (!approachRunData.resultData) {
							ret[approachRunData.approachRunId] = {
								isCompletionRun:
									approachData.completionApproachRunId ==
									approachRunData.approachRunId,
								approachRunId: approachRunData.approachRunId,
								approachtestid: approachRunData.approachtestid,
								vuapproachesid,
								rsuId
							};
						}
					})
				)
			);
			return Object.values(ret);
		},
		locationHistory: state => state.location_history,
		latestFvuGpsData: (state, getters) => {
			const locationHistory = getters.locationHistory;
			if (locationHistory.length == 1) {
				return locationHistory[0].gpsData;
			}

			var ret = null;
			var last;
			_.eachRight(locationHistory, lhItem => {
				if (last) {
					const tooClose =
						last.tooCloseTo && last.tooCloseTo == lhItem.latlngHash;
					const tooFar =
						last.tooFarFrom && last.tooFarFrom == lhItem.latlngHash;
					const stationary = last.stationary;
					if (!tooClose && !tooFar && !stationary) {
						ret = last.gpsData;
						return false;
					}
				}
				last = lhItem;
			});
			return ret || (last && last.gpsData);
		},
		latestFvuPosition: (state, getters) => {
			const gpsData = getters.latestFvuGpsData;
			if (
				!gpsData ||
				!_.isNumber(gpsData.latitude) ||
				!_.isNumber(gpsData.longitude)
			) {
				return null;
			}
			return [gpsData.latitude, gpsData.longitude];
		},
		latestFvuHeading: (state, getters) =>
			getters.latestFvuGpsData && getters.latestFvuGpsData.heading,
		latestMonitorFrame: (state, getters) => {
			const ret = state.latest_monitor_frame;
			const now = state.now || new Date().getTime();
			const age =
				ret && ret.timestamp && now - new Date(ret.timestamp).getTime();
			if (age && age >= 1000 * getters.LATEST_MONITOR_FRAME_TIMEOUT_SECS) {
				console.debug(
					MODULE_NAME +
						"/latestMonitorFrame: Returning NULL because stored frame is " +
						age +
						"ms old."
				);
				console.debug(ret);
				return null;
			}
			return ret;
		},
		latestMonitorFrameRSUs: (state, getters, rootState, rootGetters) => {
			const rsus = [];
			const latestMonitorFrame = getters.latestMonitorFrame;
			if (latestMonitorFrame && latestMonitorFrame.type) {
				_.each(latestMonitorFrame.type, typeData => {
					_.each(typeData.messages, messageData => {
						const rsu = rootGetters[`${RsuStore.MODULE_NAME}/byIdentifier`](
							messageData.rsuId
						);
						if (rsu) rsus.push(rsu);
					});
				});
			}
			return rsus;
		},
		latlngHash: () => {
			return getLatLngHash;
		},
		approachesForPoint: state => {
			return ({ vuApproachLocationId, latitude, longitude, filterFn }) => {
				var ret = [];
				filterFn = filterFn || (i => !!i);
				_.each(state.vu_approaches[vuApproachLocationId], vuApproach => {
					if (filterFn(vuApproach)) {
						_.each(vuApproach.geoJSON.coordinates, polygon => {
							if (pointInPolygon([longitude, latitude], polygon)) {
								ret.push(vuApproach);
								if (ret.length > 1) return false;
							}
						});
					}
				});
				return ret;
			};
		},
		vuApproachLocationId: state => {
			return ({ rsuId, deviceId, intersectionId, vuApproachLocationId }) => {
				if (vuApproachLocationId) {
					return vuApproachLocationId;
				}
				if (
					intersectionId &&
					state.vu_approach_location_reference.intersectionId[intersectionId]
				) {
					return state.vu_approach_location_reference.intersectionId[
						intersectionId
					];
				}
				rsuId = rsuId || deviceId;
				if (rsuId && state.vu_approach_location_reference.rsuId[rsuId]) {
					return state.vu_approach_location_reference.rsuId[rsuId];
				}
				return null;
			};
		},
		approachesByVuApproachLocationId: state => {
			return vuApproachLocationId => state.vu_approaches[vuApproachLocationId];
		},
		rsuApproaches: (state, getters, rootState, rootGetters) => {
			return rsuId => {
				const rsu = rootGetters[RsuStore.MODULE_NAME + "/byIdentifier"](rsuId);
				if (!rsu) return null;
				return getters.approachesByVuApproachLocationId(
					getters.vuApproachLocationId(rsu)
				);
			};
		},
		approachesByIntersectionId: (state, getters) => {
			return intersectionId =>
				getters.approachesByVuApproachLocationId(
					getters.vuApproachLocationId({ intersectionId })
				);
		},
		completionApproachRun: (state, getters) => {
			return ({ rsuId, deviceId, vuapproachesid }) => {
				rsuId = rsuId || deviceId;
				const approachRuns = getters.approachRuns({ rsuId, vuapproachesid });
				if (approachRuns) {
					const rsuData = getters.rsuData(rsuId);
					if (rsuData.completionApproachRunId)
						return approachRuns[rsuData.completionApproachRunId];
				}
				return null;
			};
		},
		approachRuns: (state, getters) => {
			return ({ rsuId, deviceId, vuapproachesid }) => {
				rsuId = rsuId || deviceId;
				const rsuData = getters.rsuData(rsuId);
				const approachData = rsuData && rsuData[vuapproachesid];
				return approachData && approachData.approachRuns;
			};
		},
		rsuApproachComplete: (state, getters) => {
			return ({ rsuId, vuapproachesid }) => {
				var ret = null;
				const rsuData = getters.rsuData(rsuId);
				if (rsuData && rsuData[vuapproachesid]) {
					ret =
						rsuData[vuapproachesid].approachComplete &&
						!rsuData[vuapproachesid].resultPending;
				}
				return ret;
			};
		},
		rsuAllApproachesComplete: (state, getters) => {
			return ({ rsuId, deviceId }) => {
				rsuId = rsuId || deviceId;

				const parentApproaches = Object.values(
					getters.rsuApproaches(rsuId)
				).filter(
					vuApproach =>
						vuApproach.vuapproachesid !=
							MODULE_CONFIG.FALLBACK_APPROACH.vuapproachesid &&
						!vuApproach.parentapproachid
				);
				return (
					parentApproaches.length &&
					_.reduce(
						parentApproaches,
						(r, vuApproach) => {
							// const ret = getters.rsuApproachComplete({ rsuId, ...vuApproach });
							// console.log(r && ret);
							// debugger;
							// return r && ret;
							return r && getters.rsuApproachComplete({ rsuId, ...vuApproach });
						},
						true
					)
				);
			};
		},
		rsuApproachValidationError: (state, getters) => {
			return ({ rsuId, vuapproachesid }) => {
				var ret = null;
				const rsuData = getters.rsuData(rsuId);
				if (rsuData && rsuData[vuapproachesid]) {
					const invalidApproachRuns = _.reduce(
						rsuData[vuapproachesid].approachRuns,
						(r, ar) => {
							if (!ar.validated) r.push(ar);
							return r;
						},
						[]
					);
					if (invalidApproachRuns.length) {
						invalidApproachRuns.sort((a, b) => b.timestamp - a.timestamp);
						ret = invalidApproachRuns[0].validationDetails;
					}
				}
				return ret;
			};
		},
		rsuApproachObservedTypeFrequency: (state, getters) => {
			// console.warn("rsuApp", 8);
			return ({ rsuId, vuapproachesid, type }) => {
				var ret = null;
				const rsuData = getters.rsuData(rsuId);
				try {
					if (
						rsuData &&
						rsuData[vuapproachesid] &&
						rsuData[vuapproachesid][type]
					) {
						ret =
							rsuData[vuapproachesid].gpsCount &&
							rsuData[vuapproachesid][type].parserCount /
								rsuData[vuapproachesid].gpsCount;
						// rsuData[vuapproachesid][type].freqTotal /
						// rsuData[vuapproachesid][type].freqCount;
					}
					if (isNaN(ret)) throw "NaN";
				} catch (e) {
					console.error("Error calculating rsuApproachObservedFrequency", e);
				}
				return ret;
			};
		},

		rsuApproachTimeWithin: (state, getters) => {
			return ({ rsuId, vuapproachesid }) => {
				var ret = null;
				const rsuData = getters.rsuData(rsuId);
				try {
					if (rsuData && rsuData[vuapproachesid]) {
						ret = rsuData[vuapproachesid].gpsCount * 1000;
					}
					if (isNaN(ret)) throw "NaN";
				} catch (e) {
					console.debug("Error calculating rsuApproachTimeWithin", e);
				}
				return ret;
			};
		},

		rsuData: state => {
			return rsuId => {
				return state.rsu_data[rsuId];
			};
		},

		rsuRecentSignalStrength: (state, getters, rootState, rootGetters) => {
			return ({ deviceId, rsuId }) => {
				rsuId = rsuId || deviceId;
				const rsu = rootGetters[RsuStore.MODULE_NAME + "/byIdentifier"](rsuId);
				const monitorFrame = getters.latestMonitorFrame;
				let ret;
				if (rsu && monitorFrame) {
					// Walk the monitorFrame looking for partialParser messages for this RSU with an RSSI value
					let rssi = [];
					_.each(monitorFrame.type, typeData => {
						_.each(typeData.messages, rsuData => {
							if (rsuData.rsuId == rsuId) {
								rssi.push(
									..._.reduce(
										rsuData.partialParser,
										(collectedRssi, ppData) => {
											if (_.isNumber(ppData.Rssi)) {
												collectedRssi.push(ppData.Rssi);
											}
											return collectedRssi;
										},
										[]
									)
								);
							}
						});
					});
					if (rssi.length) {
						// We have recent rssi information for this RSU. Let's map it to a 1-5 scale.
						// This transformation is based on an expected range of RSSI values that goes from roughly -100 dBm to -60 dBm.
						ret = Math.max(
							1,
							Math.min(
								5,
								Math.round(_.mean(rssi.map(i => (2.5 * (100 + i)) / 20)))
							)
						);
					}
				}
				return ret;
			};
		},

		testConfig: (state, getters) => {
			return rsuId => {
				// User defined always overrides
				if (!_.isEmpty(state.test_config)) return state.test_config;
				// Then rsu-specific config, if an rsuId was given and we have it.
				// Note that it's up to the caller to check for the isLoading flag
				const rsuTestConfig = (rsuId && getters.rsuTestConfig(rsuId)) || {};
				return Object.assign({}, getters.defaultTestConfig, rsuTestConfig);
			};
		},
		defaultTestConfig: (state, getters, globalState) => {
			const ret = {};
			const vFreqFactor = Math.pow(
				10,
				MODULE_CONFIG.VALIDATION_FREQUENCY_PRECISION
			);

			// User profile attribute for message pass rate.
			const attrKey = "vuatppass";
			let vuatppass = 0.7;

			// If profile attribute is available and parseable, then use it.
			if (!isNaN(globalState.user.profileAttributes[attrKey])) {
				vuatppass = parseFloat(globalState.user.profileAttributes[attrKey]);
			}

			// Limit vuatppass between 0 and 1.
			if (vuatppass < 0) {
				vuatppass = 0;
			}
			if (vuatppass > 1) {
				vuatppass = 1;
			}

			_.each(getters.testableMessageTypes, msgType => {
				ret[msgType.slug + "_error_threshold_pct"] = 10;
				if (msgType.expectedFrequency) {
					ret[msgType.slug + "_validation_frequency"] =
						Math.round(msgType.expectedFrequency * vuatppass * vFreqFactor) /
						vFreqFactor;
				}
			});
			return ret;
		},
		rsuTestConfig: state => {
			return rsuId => {
				return state.rsu_test_config[rsuId];
			};
		},
		dynamicTestResult: (state, getters) => {
			// Run a test on the contents of the latest data frame only, optionally limited to one RSU.
			// Used in FvuMonitorMap to color the dynamic indicator for the activeFvu.
			return ({ rsuId, deviceId }) => {
				rsuId = rsuId || deviceId;
				const monitorFrame = getters.latestMonitorFrame;

				// Init ret and dataByMsgType
				const ret = { overall: getters.TEST_RESULTS.NULL };
				const dataByMsgType = {};
				_.each(getters.testableMessageTypes, msgTypeData => {
					const msgType = msgTypeData.slug;
					ret[msgType] = getters.TEST_RESULTS.NULL;
					dataByMsgType[msgType] = {
						freqCount: 0,
						freqTotal: 0,
						idleCount: 0,
						parserErrorCount: 0,
						parserFormatErrorCount: 0,
						parserContentErrorCount: 0,
						parserCount: 0
					};
				});

				//  Loop over the "type" array in the latest frame, discarding any not related to the given rsu, if one was given, and accumulate results in dataByMsgType
				if (!monitorFrame || !monitorFrame.type || !monitorFrame.type.length) {
					return ret;
				}
				_.each(monitorFrame.type, typeFrameData => {
					const msgTypeData = DSRCmessage.get(typeFrameData.id);
					const msgType = msgTypeData && msgTypeData.slug;
					if (!msgType) return;
					_.each(typeFrameData.messages, rsuMessage => {
						// If we were asked about a specific RSU and this message is about a different one, discard it.
						if (rsuId && rsuMessage.rsuId != rsuId) return;
						_.each(["partialParser", "frequencyChecker"], srcKey =>
							_.each(rsuMessage[srcKey], srcData => {
								setErrorUnheardFlags(srcData);
								switch (srcKey) {
									case "frequencyChecker":
										srcData.WindowSizeSeconds = srcData.WindowSizeSeconds || 1;
										if (srcData.isUnheard) {
											dataByMsgType[msgType].idleCount++;
										} else {
											dataByMsgType[msgType].freqCount +=
												srcData.WindowSizeSeconds;
											dataByMsgType[msgType].freqTotal +=
												srcData.FrequencyHz * srcData.WindowSizeSeconds;
										}
										break;
									case "partialParser":
										dataByMsgType[msgType].parserCount++;
										if (srcData.isError) {
											dataByMsgType[msgType].parserErrorCount++;
											if (!srcData.IsCorrectFormat) {
												dataByMsgType[msgType].parserFormatErrorCount++;
											}
											if (!srcData.IsCorrectContent) {
												dataByMsgType[msgType].parserContentErrorCount++;
											}
										}
										break;
								}
							})
						);
					});
				});

				//  Then use the accumulated data to determine a TEST_RESULT for each message type, then put them together for an overall
				const resultCounts = _.reduce(
					getters.TEST_RESULTS,
					(r, v, k) => {
						r[k] = 0;
						return r;
					},
					{}
				);
				_.each(getters.testableMessageTypes, msgTypeData => {
					const msgType = msgTypeData.slug;
					ret[msgType] = getters.msgTypeTestResult({
						rsuId,
						msgType,
						data: dataByMsgType[msgType]
					});
					resultCounts[ret[msgType]]++;
				});
				if (resultCounts[getters.TEST_RESULTS.FAIL]) {
					ret.overall = getters.TEST_RESULTS.FAIL;
				} else if (resultCounts[getters.TEST_RESULTS.UNHEARD]) {
					ret.overall = getters.TEST_RESULTS.UNHEARD;
				} else if (resultCounts[getters.TEST_RESULTS.PASS]) {
					ret.overall = getters.TEST_RESULTS.PASS;
				}

				return ret;
			};
		},
		rsuTestResult: (state, getters) => {
			return ({ rsuId }) => {
				const data = state.rsu_data[rsuId];
				var ret = {
					overall: getters.TEST_RESULTS.NULL,
					counts: { latlng: { total: 0 }, msgType: { total: 0 } },
					testStart: null,
					testEnd: null,
					latlngResults: {},
					msgTypeResults: {}
				};
				_.each(getters.TEST_RESULTS, v => {
					ret.counts.latlng[v] = 0;
					ret.counts.msgType[v] = 0;
				});
				if (!data) return ret;
				_.each(getters.testableMessageTypes, msgTypeData => {
					const res = getters.msgTypeTestResult({
						rsuId,
						msgType: msgTypeData.slug
					});
					ret.msgTypeResults[msgTypeData.slug] = res;
					ret.counts.msgType[res]++;
					ret.counts.msgType.total++;
				});
				_.each(data, (latlngData, latlngHash) => {
					const res = getters.latlngTestResult({ rsuId, latlngHash });
					if (res.testStart) {
						ret.testStart = _.min([ret.testStart, res.testStart]);
					}
					if (res.testEnd) {
						ret.testEnd = _.max([ret.testEnd, res.testEnd]);
					}
					ret.latlngResults[latlngHash] = res;
					ret.counts.latlng[res.overall]++;
					ret.counts.latlng.total++;
				});
				if (ret.counts.msgType[getters.TEST_RESULTS.FAIL]) {
					ret.overall = getters.TEST_RESULTS.FAIL;
				} else if (ret.counts.msgType[getters.TEST_RESULTS.UNHEARD]) {
					ret.overall = getters.TEST_RESULTS.UNHEARD;
				} else if (ret.counts.msgType[getters.TEST_RESULTS.PASS]) {
					ret.overall = getters.TEST_RESULTS.PASS;
				}

				return ret;
			};
		},
		latlngTestResult: (state, getters) => {
			return ({ rsuId, latlngHash }) => {
				const data = state.rsu_data[rsuId] && state.rsu_data[rsuId][latlngHash];
				const overrideResult = data && data.testResult;
				var ret = {
					overall: overrideResult || getters.TEST_RESULTS.NULL,
					counts: { total: 0 },
					msgTypeResults: {}
				};
				_.each(getters.TEST_RESULTS, v => (ret.counts[v] = 0));
				if (!data) return ret;
				_.each(
					[
						"approachComplete",
						"completionApproachRunId",
						"resultPending",
						"resultLoading",
						"resultLoaded"
					],
					key => {
						if (!_.isUndefined(data[key])) {
							ret[key] = data[key];
						}
					}
				);
				ret.testStart = data.enteredAt;
				ret.testEnd = data.exitedAt;

				_.each(getters.testableMessageTypes, msgTypeData => {
					const msgType = msgTypeData.slug;
					const res = getters.msgTypeTestResult({ rsuId, latlngHash, msgType });
					ret.msgTypeResults[msgType] = res;
					ret.counts[res]++;
					ret.counts.total++;
				});
				if (overrideResult) {
					ret.overall = overrideResult;
				} else if (ret.counts[getters.TEST_RESULTS.FAIL]) {
					ret.overall = getters.TEST_RESULTS.FAIL;
				} else if (ret.counts[getters.TEST_RESULTS.UNHEARD]) {
					ret.overall = getters.TEST_RESULTS.UNHEARD;
				} else if (ret.counts[getters.TEST_RESULTS.PASS]) {
					ret.overall = getters.TEST_RESULTS.PASS;
				}
				return ret;
			};
		},
		msgTypeTestResult: (state, getters, rootState, rootGetters) => {
			return ({ rsuId, latlngHash, msgType, data }) => {
				const msgTypeData = DSRCmessage.get(msgType);
				if (!data) {
					data =
						msgTypeData &&
						(latlngHash
							? state.rsu_data[rsuId] &&
							  state.rsu_data[rsuId][latlngHash] &&
							  state.rsu_data[rsuId][latlngHash][msgTypeData.slug]
							: getters.flattenedRsuData(rsuId)[msgTypeData.slug]);
				}
				if (data && data.testResult) {
					return data.testResult;
				}
				const shouldHaveType = rsuId
					? rootGetters[RsuStore.MODULE_NAME + "/shouldHaveType"]({
							rsuId,
							msgType
					  })
					: null;
				if (!data || !msgTypeData) {
					return shouldHaveType
						? getters.TEST_RESULTS.UNHEARD
						: getters.TEST_RESULTS.NULL;
				}
				if (msgTypeData.expectedFrequency) {
					if (!data.parserCount) {
						return getters.TEST_RESULTS.UNHEARD;
					}
					const validationFrequency = getters.testConfig(rsuId)[
						msgTypeData.slug + "_validation_frequency"
					];
					// AVERAGE frequency for this rsu[/location]/msgtype
					const avgFreq = data.gpsCount
						? data.parserCount / data.gpsCount
						: // Fall back on frequencyChecker data when we don't have gpsCount (e.g. in the dynamic test)
						data.freqCount
						? data.freqTotal / data.freqCount
						: // If we have no gpsCount AND no frequencyChecker data, assume this is one monitor frame
						  data.parserCount;

					if (avgFreq < validationFrequency) {
						return getters.TEST_RESULTS.FAIL;
					}
				}
				const errorThresholdPct = getters.testConfig(rsuId)[
					msgTypeData.slug + "_error_threshold_pct"
				];
				if (!data.parserCount) {
					return getters.TEST_RESULTS.NULL;
				}
				if (
					data.parserErrorCount / data.parserCount >=
					errorThresholdPct / 100
				) {
					return getters.TEST_RESULTS.FAIL;
				}

				return getters.TEST_RESULTS.PASS;
			};
		},
		averageFrequency: (state, getters) => {
			return ({ rsuId, msgType, vuapproachesid }) => {
				if (!rsuId) {
					throw new Error(
						`${MODULE_NAME}/averageFrequency: Missing or invalid rsuid: "${rsuId}"`
					);
				}
				const msgTypeData = DSRCmessage.get(msgType);
				if (!msgTypeData) {
					throw new Error(
						`${MODULE_NAME}/averageFrequency: Missing or invalid msgType: "${msgType}"`
					);
				}
				const allData = vuapproachesid
					? getters.rsuData(rsuId)[vuapproachesid]
					: getters.flattenedRsuData(rsuId);
				const data = allData && allData[msgType];
				if (!data) return null;
				if (data.gpsCount) {
					return data.parserCount / data.gpsCount;
				} else if (data.freqCount) {
					return data.freqTotal / data.freqCount;
				} else if (data.parserCount) {
					return data.parserCount;
				} else {
					return null;
				}
			};
		},
		flattenedRsuData: (state, getters) => {
			return rsuId => {
				const rsuData = getters.rsuData(rsuId);
				const ret = {
					gpsCount: 0,
					freqCount: 0,
					freqTotal: 0,
					idleCount: 0,
					parserErrorCount: 0,
					parserFormatErrorCount: 0,
					parserContentErrorCount: 0,
					parserCount: 0,
					latestTimestamp: null
				};

				_.each(rsuData, latlngData => {
					ret.gpsCount += latlngData.gpsCount;
					ret.freqCount += latlngData.freqCount;
					ret.freqTotal += latlngData.freqTotal;
					ret.idleCount += latlngData.idleCount;
					ret.parserErrorCount += latlngData.parserErrorCount;
					ret.parserFormatErrorCount += latlngData.parserFormatErrorCount;
					ret.parserContentErrorCount += latlngData.parserContentErrorCount;
					ret.parserCount += latlngData.parserCount;
					ret.latestTimestamp = _.max([
						ret.latestTimestamp,
						latlngData.latestTimestamp
					]);
					_.each(latlngData, (val, key) => {
						const msgType = DSRCmessage.get(key);
						if (!msgType) return;
						if (!ret[key]) {
							ret[key] = {
								gpsCount: 0,
								freqCount: 0,
								freqTotal: 0,
								idleCount: 0,
								parserErrorCount: 0,
								parserFormatErrorCount: 0,
								parserContentErrorCount: 0,
								parserCount: 0
							};
						}
						ret[key].gpsCount += latlngData.gpsCount;
						ret[key].freqCount += val.freqCount;
						ret[key].freqTotal += val.freqTotal;
						ret[key].idleCount += val.idleCount;
						ret[key].parserErrorCount += val.parserErrorCount;
						ret[key].parserFormatErrorCount += val.parserFormatErrorCount;
						ret[key].parserContentErrorCount += val.parserContentErrorCount;
						ret[key].parserCount += val.parserCount;
					});
				});
				return ret;
			};
		},
		needsCleanup: (state, getters) => {
			return getters.locationHistory.length > MODULE_CONFIG.MAX_LOCATIONS;
		}
	}
};

function getLatLngHash(payload) {
	var latHash, lngHash;
	if (payload.LatHash && payload.LngHash) {
		latHash = payload.LatHash;
		lngHash = payload.LngHash;
	} else {
		const latlng = payload.gpsData;
		const lat = latlng.latitude || latlng.lat || lat[0];
		const lng = latlng.longitude || latlng.lng || lat[1];
		latHash =
			Math.round(lat / MODULE_CONFIG.HASH_RESOLUTION) *
			MODULE_CONFIG.HASH_RESOLUTION;
		lngHash =
			Math.round(lng / MODULE_CONFIG.HASH_RESOLUTION) *
			MODULE_CONFIG.HASH_RESOLUTION;
	}
	return [latHash, lngHash].join(",");
}

function setErrorUnheardFlags(o) {
	if (!_.isUndefined(o.FrequencyHz)) {
		// This is frequencyChecker data: All we can do is flag the idle ones so we don't accumulate them into the totals
		o.isUnheard = !o.FrequencyHz;
	} else {
		// This is partialParser data
		o.isError = !o.IsCorrectFormat || !o.IsCorrectContent;
	}
}

function gpsDifferentEnough(d1, d2) {
	return getLatLngHash(d1) != getLatLngHash(d2);
}

function degToMeters(d) {
	return d * 110250;
}

function metersToMiles(m) {
	return m * 0.000621371;
}

function milesToMeters(mi) {
	return mi / metersToMiles(1);
}

function msToHours(ms) {
	return ms / 1000 / 60 / 60;
}

function gpsTooDifferent(d1, d2) {
	const dist = degToMeters(
		Geo.flattenedEuclideanDistance(d1.gpsData, d2.gpsData)
	);
	const rate = Math.max(d1.gpsData.speed, d2.gpsData.speed);
	const time = d2.timestamp - d1.timestamp;
	if (time > MODULE_CONFIG.GPS_MAX_CONTINUOUS_TIME_SEC * 1000) {
		return true;
	}
	const maxDist =
		MODULE_CONFIG.GPS_MAX_DISTANCE_FUDGE_FACTOR *
		milesToMeters(rate * msToHours(time)); // Generous fudge factor
	return dist > maxDist;
}
